// routes.js
// meta.site picks the site a route belongs to (see services/apiConfig.js):
//   'website'  → <root>/...           public landing website
//   'admin'    → <root>/admin/...     admin panel
//   'facility' → <root>/facility/...  company / facility partner portal
// Paths are relative to the site, e.g. '/dashboard' with site 'admin' is <root>/admin/dashboard.
import { lazy } from 'react';
import Dashboard from '../pages/Dashboard';
import Login from '../pages/Login';
import { Navigate, useParams } from 'react-router-dom';

import BookingsList from '../pages/bookings/List';
import BookingDetails from '../pages/bookings/Details';



import CategoryList from '../pages/categories/List';
import AddCategory from '../pages/categories/AddUpdate';
import SubCategories from '../pages/categories/SubCategories';

import EmirateList from '../pages/emirates/List';
import AddEmirate from '../pages/emirates/AddUpdate';

import CompanyList from '../pages/comapnies/List';
import AddCompany from '../pages/comapnies/AddUpdate';
import CompanyDetails from '../pages/comapnies/Details';

import BannerList from '../pages/banners/List';
import AddBanner from '../pages/banners/AddUpdate';

import PackageList from '../pages/packages/List';
import AddPackage from '../pages/packages/AddUpdate';
import SubscriptionHistory from '../pages/packages/SubscriptionHistory';



import SystemUserList from '../pages/system_users/List';
import AddSystemUser from '../pages/system_users/AddUpdate';

import RoleList from '../pages/roles/List';
import AddRole from '../pages/roles/AddUpdate';

import Account from '../pages/Account';
import UserList from '../pages/users/List';
import UserDetails from '../pages/users/Details';
import PaymentRequestsList from '../pages/payment-requests/List';
import PropertiesList from '../pages/properties/List';

// Company / Facility portal
import PortalLogin from '../pages/portal/Login';
import PortalDashboard from '../pages/portal/Dashboard';
import PortalServiceList from '../pages/portal/services/List';
import PortalServiceAddUpdate from '../pages/portal/services/AddUpdate';
import PortalProfile from '../pages/portal/Profile';
import PortalBookings from '../pages/portal/bookings/List';
import PortalBookingDetails from '../pages/portal/bookings/Details';
import NotFound from '../pages/NotFound'; 
import Setting from '../pages/Setting';

// Landing website — lazy, so its pages (and their CSS / scripts) only load on the website
const HomePage = lazy(() => import('../pages/website/HomePage'));
const TermsAndConditions = lazy(() => import('../pages/website/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('../pages/website/PrivacyPolicy'));
const CancellationPolicy = lazy(() => import('../pages/website/CancellationPolicy'));
// Support.jsx and BecomePartner.jsx are fully commented out, so they are not routed yet.



// Facility Management reuses the Service Provider screens with type="facility"
const FacilityList = () => <CompanyList type="facility" />;
const FacilityAddUpdate = () => <AddCompany type="facility" />;
const FacilityDetails = () => <CompanyDetails type="facility" />;

// Old Requests / Jobs URLs now live under Bookings
const ToBookings = () => <Navigate to="/bookings" replace />;
const ToBookingDetails = () => <Navigate to={`/bookings/${useParams().id}`} replace />;

const routes = [
    // ---------- Landing website: <root>/ ----------
    { path: '/', component: HomePage, meta: { site: 'website' } },
    { path: '/terms', component: TermsAndConditions, meta: { site: 'website' } },
    { path: '/privacy', component: PrivacyPolicy, meta: { site: 'website' } },
    { path: '/cancellation-policy', component: CancellationPolicy, meta: { site: 'website' } },

    // ---------- Admin panel: <root>/admin/ ----------
    {
        path: '/dashboard',
        component: Dashboard,
        meta: { site: 'admin', requiresAuth: true, hideHeader: false, permission: 'view-dashboard' }
    },
    {
        path: '/bookings',
        component: BookingsList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'list-requests' }
    },
    {
        path: '/bookings/:id',
        component: BookingDetails,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'list-requests' }
    },
    { path: '/requests', component: ToBookings, meta: { site: 'admin', requiresAuth: true, hideHeader: false } },
    { path: '/requests/:id', component: ToBookingDetails, meta: { site: 'admin', requiresAuth: true, hideHeader: false } },
    { path: '/jobs', component: ToBookings, meta: { site: 'admin', requiresAuth: true, hideHeader: false } },
    { path: '/jobs/:id', component: ToBookingDetails, meta: { site: 'admin', requiresAuth: true, hideHeader: false } },
    {
        path: '/login',
        component: Login,
        meta: { site: 'admin', requiresAuth: false, hideHeader: true }
    },
    {
        path: '/companies',
        component: CompanyList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false,  permission:'list-companies' }
    },
    {
        path: '/companies/create/:id?',
        component: AddCompany,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission:'create-companies' }
    },

    {
        path: '/companies/:id',
        component: CompanyDetails,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false , permission:'list-companies' }
    },
  

    {
        path: '/facilities',
        component: FacilityList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'list-facilities' }
    },
    {
        path: '/facilities/create/:id?',
        component: FacilityAddUpdate,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'create-facilities' }
    },
    {
        path: '/facilities/:id',
        component: FacilityDetails,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'list-facilities' }
    },

    {
        path: '/banners/',
        component: BannerList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false , permission:'list-banners' }
    },
    {
        path: '/banners/create/:id?',
        component: AddBanner,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false , permission:'create-banners' }
    },

    {
        path: '/packages/',
        component: PackageList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false  , permission:'list-packages' }
    },
    {
        path: '/packages/create/:id?',
        component: AddPackage,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false  , permission:'create-packages' }
    },
    {
        path: '/subscription-history/',
        component: SubscriptionHistory,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false  , permission:'list-subscriptions' }
    },
  
    {
        path: '/categories/',
        component: CategoryList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false  , permission:'list-categories' }
    },
    {
        path: '/categories/create/:id?',
        component: AddCategory,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false  , permission:'create-categories' }
    },
    {
        path: '/categories/:id?',
        component: SubCategories,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false  , permission:'create-categories' }
    },

    {
        path: '/emirates/',
        component: EmirateList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false  , permission:'list-emirates' }
    },
    {
        path: '/emirates/create/:id?',
        component: AddEmirate,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false  , permission:'create-emirates' }
    },

    {
        path: '/users/',
        component: UserList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'list-users' }
    },
    {
        path: '/users/:id',
        component: UserDetails,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'list-users' }
    },
    {
        path: '/payment-requests',
        component: PaymentRequestsList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'list-payment-requests' }
    },
    {
        path: '/properties',
        component: PropertiesList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'list-properties' }
    },
   
    {
        path: '/system-users/',
        component: SystemUserList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'list-system-users' }
    },
    {
        path: '/system-users/create/:id?',
        component: AddSystemUser,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'create-system-users' }
    },
    {
        path: '/roles/',
        component: RoleList,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'list-roles' }
    },
    {
        path: '/roles/create/:id?',
        component: AddRole,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'create-roles' }
    },
    {
        path: '/account/',
        component: Account,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false }
    },
    {
        path: '/settings/',
        component: Setting,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false, permission: 'manage-settings' }
    },

    {
        path: '*',
        component: NotFound,
        meta: { site: 'admin', requiresAuth: true , hideHeader: false }
    },

    // ---------- Company / Facility portal: <root>/facility/ (company session) ----------
    {
        path: '/login',
        component: PortalLogin,
        meta: { requiresAuth: false, hideHeader: true, site: 'facility' }
    },
    {
        path: '/dashboard',
        component: PortalDashboard,
        meta: { requiresAuth: true, hideHeader: false, site: 'facility' }
    },
    {
        path: '/services',
        component: PortalServiceList,
        meta: { requiresAuth: true, hideHeader: false, site: 'facility' }
    },
    {
        path: '/services/create/:id?',
        component: PortalServiceAddUpdate,
        meta: { requiresAuth: true, hideHeader: false, site: 'facility' }
    },
    {
        path: '/bookings',
        component: PortalBookings,
        meta: { requiresAuth: true, hideHeader: false, site: 'facility' }
    },
    {
        path: '/bookings/:id',
        component: PortalBookingDetails,
        meta: { requiresAuth: true, hideHeader: false, site: 'facility' }
    },
    {
        path: '/profile',
        component: PortalProfile,
        meta: { requiresAuth: true, hideHeader: false, site: 'facility' }
    },

];

export default routes;
